<script>
import { states } from '@utils/consts/states'

export default {
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number, Object, Array],
      default: '',
    },
    editSelectValues: {
      type: [Object, Array],
      default: null,
    },
    valueArray: {
      type: Array,
      default: () => [],
    },
    readOnlyValues: {
      type: Array,
      default: () => [],
    },
    editMode: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    error: {
      type: String,
      default: '',
    },
    rules: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      modelValue: this.value,
      stateOptions: [],
    }
  },
  computed: {
    isRequired() {
      return this.rules.includes('required')
    },
  },
  watch: {
    value: {
      handler: function (newVal) {
        this.modelValue = newVal
      },
    },
    editSelectValues: {
      handler: function () {
        this.updateEditSelectedValues()
      },
    },
  },
  created() {
    this.updateEditSelectedValues()
  },
  methods: {
    updateEditSelectedValues() {
      this.stateOptions = this.editSelectValues
        ? this.editSelectValues.map((stateValue) => ({
            number: '',
            state: stateValue,
          }))
        : states.map((stateValue) => ({
            number: '',
            state: stateValue,
          }))
    },
    onInput(inputEvent) {
      let value = inputEvent
      if (inputEvent instanceof Event) {
        value = inputEvent.target?.value || null
      }
      this.$emit('update:value', value)
    },
    isRemovable(state) {
      return !this.readOnlyValues.some((readOnlyState) => readOnlyState.state === state)
    },
  },
}
</script>

<template>
  <div :class="$style.container">
    <p :class="$style.label" class="p1">
      {{ label }}:
      <span v-if="isRequired && editMode" data-required-asterisk :class="$style.requiredAsterisk">*</span>
    </p>

    <template v-if="!editMode">
      <ul v-if="value.length" :class="$style.valuesList">
        <li v-for="item in value" :key="item.id ? item.id : item.state">
          {{ item.state }}
          <template v-if="item.number">- {{ item.number }}</template>
        </li>
      </ul>
      <h4 v-else :class="$style.value">{{ 'N/A' }}</h4>
    </template>

    <template v-if="editMode">
      <ValidationProvider v-slot="{ errors, changed }" :debounce="300" :name="label" :rules="rules">
        <BaseMultiSelect
          v-model="modelValue"
          :options="stateOptions"
          option-text="state"
          :disabled="disabled || stateOptions.length === 0"
          :placeholder="placeholder"
          :changed="changed"
          @input="onInput">
          <template v-slot:tag="{ option, remove }">
            <span
              :class="isRemovable(option.state) ? '' : 'multiselect__tag-readonly'"
              class="multiselect__tag"
              @mousedown.prevent>
              <span>{{ option.state }}</span>
              <i v-if="isRemovable(option.state)" class="multiselect__tag-icon" @click.stop="remove(option)"></i>
            </span>
          </template>
        </BaseMultiSelect>
        <p v-if="errors[0]" :class="$style.errorMessage" class="tag-text">{{ errors[0] }}</p>
      </ValidationProvider>
      <div v-for="state in modelValue" :key="state.state" :class="$style.licenseNumberInputContainer">
        <ValidationProvider v-slot="{ changed: stateChanged }" class="d-contents" :debounce="300" :name="state.state">
          <p :class="$style.licenseNumberLabel" class="p1">{{ state.state + ': ' }}</p>
          <BaseInputText
            v-model="state.number"
            class="ml-1"
            :disabled="disabled || readOnlyValues.some((readOnlyState) => readOnlyState.state === state.state)"
            :name="state.state + ' - '"
            :changed="stateChanged"
            maxlength="30"
            placeholder="License Number" />
        </ValidationProvider>
      </div>
    </template>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.container {
  overflow-y: initial;

  div[class*='errorContainer'] {
    position: relative;
  }
}

.licenseNumberInputContainer {
  display: flex;
  margin-top: 10px;

  input {
    margin-bottom: 10px;
  }
}

.licenseNumberLabel {
  margin: 10px 10px 10px 0;
  color: $dark-gray;
}

.label {
  margin-top: 10px;
  margin-bottom: 10px;
  color: $dark-gray;
}

.value {
  font-weight: 700;
  color: $primary-blue;
  overflow-wrap: break-word;
}

.valuesList {
  padding-left: 0;
  font-weight: 700;
  list-style: none;

  li {
    margin-bottom: 5px;
    font-size: 16px;
    font-weight: 700;
    color: $primary-blue;
  }
}

.requiredAsterisk {
  font-weight: 700;
  color: $secondary-red;
}

.formItemSubLabel {
  margin: -8px 0 6px;
  color: $secondary-light-blue;
}

.errorMessage {
  @extend %error-message;

  text-align: right;
}
</style>
